// owner: agents-domain agent — zod schemas + inferred types shared by api and web
import { z } from 'zod';
import { AGENT_GRANTABLE_SCOPES, ALL_SCOPES, type Scope } from '../scopes';
import { CentsSchema, EpochMsSchema, UlidSchema } from './common';

const ScopeEnum = z.enum(ALL_SCOPES as [Scope, ...Scope[]]);

/** A scope an agent token may hold (never agents:manage / admin:all). */
export const AgentGrantableScopeSchema = z.string().superRefine((s, ctx) => {
  if (!(ALL_SCOPES as string[]).includes(s)) {
    ctx.addIssue({ code: 'custom', message: `Unknown scope: ${s}` });
  } else if (!(AGENT_GRANTABLE_SCOPES as string[]).includes(s)) {
    ctx.addIssue({ code: 'custom', message: `Scope not grantable to agents: ${s}` });
  }
});

// ───────────── tokens ─────────────

/** Server-enforced guardrails (see docs/agents.md "Guardrails"). */
export const DailyActionCapSchema = z.number().int().min(1).max(100_000);
export const AllowedOrgIdsSchema = z
  .array(UlidSchema)
  .min(1)
  .max(200)
  .refine((a) => new Set(a).size === a.length, 'Duplicate org ids');
const GuardrailFields = {
  /** Max outward actions (apply, message, post, campaign activate/budget) per UTC day. null = no cap. */
  dailyActionCap: DailyActionCapSchema.nullable().optional(),
  /** Orgs the agent may act on (apply to their jobs, post as, run campaigns for). null = any. */
  allowedOrgIds: AllowedOrgIdsSchema.nullable().optional(),
};

/** Rotation may change guardrails: omitted field = keep, null = clear. */
export const RotateAgentTokenBodySchema = z.object(GuardrailFields).strict();
export type RotateAgentTokenBody = z.infer<typeof RotateAgentTokenBodySchema>;

export const CreateAgentTokenBodySchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    scopes: z
      .array(AgentGrantableScopeSchema)
      .min(1)
      .max(ALL_SCOPES.length)
      .refine((a) => new Set(a).size === a.length, 'Duplicate scopes'),
    /** Max spend (cents) per gated action without human approval. null = every spend needs approval. */
    spendLimitCents: CentsSchema.nullable().optional(),
    /** Absolute expiry (epoch ms) ... */
    expiresAt: EpochMsSchema.optional(),
    /** ... or relative expiry. Omit both for a non-expiring token. */
    expiresInDays: z.number().int().min(1).max(3650).optional(),
    /** Restrict the token to one org the owner belongs to. */
    orgId: UlidSchema.optional(),
    /**
     * Add DEFAULT_AGENT_SCOPES (notifications:read: the token's own inbox) to `scopes`. Default true;
     * pass false to create a token without an inbox.
     */
    includeDefaultScopes: z.boolean().default(true),
    ...GuardrailFields,
  })
  .refine((b) => !(b.expiresAt !== undefined && b.expiresInDays !== undefined), {
    message: 'Pass expiresAt or expiresInDays, not both',
    path: ['expiresAt'],
  });
export type CreateAgentTokenBody = z.infer<typeof CreateAgentTokenBodySchema>;

export const AgentTokenSchema = z.object({
  id: UlidSchema,
  name: z.string(),
  prefix: z.string(),
  orgId: UlidSchema.nullable(),
  scopes: z.array(ScopeEnum),
  spendLimitCents: CentsSchema.nullable(),
  dailyActionCap: z.number().int().nullable(),
  allowedOrgIds: z.array(UlidSchema).nullable(),
  createdAt: EpochMsSchema,
  expiresAt: EpochMsSchema.nullable(),
  lastUsedAt: EpochMsSchema.nullable(),
  revokedAt: EpochMsSchema.nullable(),
  status: z.enum(['active', 'revoked', 'expired']),
});
export type AgentToken = z.infer<typeof AgentTokenSchema>;

/** Returned ONCE on create/rotate: the only time the plaintext secret is shown. */
export const CreatedAgentTokenSchema = AgentTokenSchema.extend({
  token: z.string().startsWith('fl_agt_'),
  rotatedFrom: UlidSchema.optional(),
});
export type CreatedAgentToken = z.infer<typeof CreatedAgentTokenSchema>;

export const AgentTokenListSchema = z.object({ items: z.array(AgentTokenSchema) });

export const GrantableScopeListSchema = z.object({
  items: z.array(z.object({ scope: ScopeEnum, description: z.string() })),
});

// ───────────── approvals ─────────────

export const ApprovalStatusSchema = z.enum([
  'pending',
  'approved',
  'rejected',
  'expired',
  'executed',
]);
export type ApprovalStatus = z.infer<typeof ApprovalStatusSchema>;

/** A pending request past its expiry is reported as expired even before a sweep persists it. */
export function approvalEffectiveStatus(
  status: ApprovalStatus,
  expiresAt: number,
  now: number,
): ApprovalStatus {
  return status === 'pending' && expiresAt <= now ? 'expired' : status;
}

export const ApprovalExecutionSchema = z.object({
  executedAt: EpochMsSchema,
  executedBy: UlidSchema.nullable(),
  result: z.record(z.string(), z.unknown()),
});

export const ApprovalSchema = z.object({
  id: UlidSchema,
  status: ApprovalStatusSchema,
  action: z.string(),
  entityKind: z.string().nullable(),
  entityId: z.string().nullable(),
  orgId: z.string().nullable(),
  amountCents: z.number().int().nullable(),
  reason: z.string(),
  payload: z.record(z.string(), z.unknown()),
  agentTokenId: UlidSchema.nullable(),
  createdAt: EpochMsSchema,
  expiresAt: EpochMsSchema,
  decidedAt: EpochMsSchema.nullable(),
  decidedBy: UlidSchema.nullable(),
  /** True when a registered handler executes the action on approval; false = the agent retries. */
  executesOnApproval: z.boolean(),
  execution: ApprovalExecutionSchema.nullable(),
});
export type Approval = z.infer<typeof ApprovalSchema>;

export const ApprovalListQuerySchema = z.object({
  status: ApprovalStatusSchema.optional(),
  action: z.string().min(1).max(100).optional(),
  cursor: z.string().min(1).max(512).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const ApprovalListSchema = z.object({
  items: z.array(ApprovalSchema),
  nextCursor: z.string().nullable(),
});

export const DecideApprovalBodySchema = z
  .object({ note: z.string().max(1000).optional() })
  .default({});

export const ApproveResponseSchema = z.object({
  approval: ApprovalSchema,
  execution: z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('executed'), result: z.record(z.string(), z.unknown()) }),
    z.object({ mode: z.literal('deferred'), note: z.string() }),
  ]),
});

// ───────────── agent actions (approval-gated built-ins + registered handlers) ─────────────

export const AgentActionBodySchema = z.object({
  type: z.string().regex(/^[a-z][a-z0-9_.]{1,99}$/, 'snake_case action type, e.g. apply_job'),
  payload: z.record(z.string(), z.unknown()),
  /** Retry with an approval id once a human approved (deferred-mode handlers only). */
  approvalId: UlidSchema.optional(),
});
export type AgentActionBody = z.infer<typeof AgentActionBodySchema>;

export const AgentActionResultSchema = z.object({
  status: z.literal('executed'),
  type: z.string(),
  result: z.record(z.string(), z.unknown()),
});

export const ActionTypeListSchema = z.object({
  items: z.array(
    z.object({
      type: z.string(),
      scope: ScopeEnum,
      irreversible: z.boolean(),
      description: z.string(),
      payloadSchema: z.record(z.string(), z.unknown()),
    }),
  ),
});

export const SendMessageBulkPayloadSchema = z.object({
  recipientUserIds: z
    .array(UlidSchema)
    .min(1)
    .max(200)
    .refine((a) => new Set(a).size === a.length, 'Duplicate recipients'),
  body: z.string().trim().min(1).max(5000),
  subject: z.string().trim().max(200).optional(),
});
export const ApplyJobPayloadSchema = z.object({
  jobId: UlidSchema,
  cover: z.string().max(10_000).default(''),
});
export const AdBudgetIncreasePayloadSchema = z.object({
  campaignId: UlidSchema,
  newBudgetCents: CentsSchema,
});
export const CampaignActivatePayloadSchema = z.object({ campaignId: UlidSchema });

// ───────────── audit ─────────────

export const AuditQuerySchema = z
  .object({
    agentTokenId: UlidSchema.optional(),
    action: z.string().min(1).max(100).optional(),
    method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS']).optional(),
    status: z.coerce.number().int().min(100).max(599).optional(),
    entityKind: z.string().min(1).max(64).optional(),
    entityId: z.string().min(1).max(128).optional(),
    /** 'agent' = rows written for an agent token; 'human' = the owner acting in a session. */
    actor: z.enum(['agent', 'human']).optional(),
    from: z.coerce.number().int().nonnegative().optional(),
    to: z.coerce.number().int().nonnegative().optional(),
    cursor: z.string().min(1).max(512).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .refine((q) => q.from === undefined || q.to === undefined || q.from <= q.to, {
    message: 'from must be <= to',
    path: ['from'],
  });
export type AuditQuery = z.infer<typeof AuditQuerySchema>;

export const AuditEntrySchema = z.object({
  id: UlidSchema,
  ts: EpochMsSchema,
  actorUserId: z.string().nullable(),
  agentTokenId: z.string().nullable(),
  action: z.string(),
  method: z.string().nullable(),
  path: z.string().nullable(),
  status: z.number().int().nullable(),
  entityKind: z.string().nullable(),
  entityId: z.string().nullable(),
  requestId: z.string().nullable(),
  meta: z.record(z.string(), z.unknown()),
});
export const AuditListSchema = z.object({
  items: z.array(AuditEntrySchema),
  nextCursor: z.string().nullable(),
});

// ───────────── MCP / JSON-RPC 2.0 ─────────────

export const JsonRpcIdSchema = z.union([z.string().max(256), z.number().int()]);
export const JsonRpcRequestSchema = z.object({
  jsonrpc: z.literal('2.0'),
  id: JsonRpcIdSchema.optional(),
  method: z.string().min(1).max(200),
  params: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]).optional(),
});
export type JsonRpcRequest = z.infer<typeof JsonRpcRequestSchema>;

export const McpToolCallParamsSchema = z.object({
  name: z.string().min(1).max(100),
  arguments: z.record(z.string(), z.unknown()).default({}),
});
export const McpResourceReadParamsSchema = z.object({ uri: z.string().min(1).max(500) });
export const McpPromptGetParamsSchema = z.object({
  name: z.string().min(1).max(100),
  arguments: z.record(z.string(), z.string()).default({}),
});
